import { KanbanBoard, type KanbanColumn, type KanbanTransition } from "@/components/kanban/KanbanBoard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MoneyDisplay } from "@/components/accounting/money-display";
import { useWorkspacePage } from "@/components/workspace/use-workspace-page";
import {
	useApprovePlannedAction,
	useListPlannedActions,
} from "@/hooks/use-manufacturing";
import type { PlannedAction } from "@/gen/omnios/v1/manufacturing_pb";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/manufacturing/board")({
	component: PlanningBoardPage,
});

const COLUMNS: KanbanColumn[] = [
	{ id: "planned", label: "Запланировано", color: "#6366f1" },
	{ id: "approved", label: "Утверждено", color: "#22c55e" },
	{ id: "executed", label: "Выполнено", color: "#64748b" },
	{ id: "cancelled", label: "Отменено", color: "#ef4444" },
];

const TRANSITIONS: KanbanTransition[] = [
	{ fromId: "planned", toId: "approved" },
	{ fromId: "planned", toId: "cancelled" },
	{ fromId: "approved", toId: "executed" },
	{ fromId: "approved", toId: "cancelled" },
];

function ActionCard({ action }: { action: PlannedAction }) {
	const { t } = useTranslation("manufacturing");

	const urgencyColor =
		action.urgency === "critical"
			? "border-l-red-500"
			: action.urgency === "high"
				? "border-l-amber-500"
				: action.urgency === "normal"
					? "border-l-blue-400"
					: "border-l-gray-300";

	return (
		<div
			className={`rounded-md border border-l-4 ${urgencyColor} bg-background p-2 text-xs`}
		>
			<div className="flex items-center justify-between">
				<span className="font-medium">
					{t(`type_${action.actionType}`)}
				</span>
				<Badge variant="outline" className="text-[10px] h-4">
					{action.urgency}
				</Badge>
			</div>
			<div className="mt-1 text-muted-foreground truncate">
				{action.productId?.slice(0, 8)}
			</div>
			<div className="mt-1 flex items-center justify-between">
				<span className="tabular-nums font-medium">
					{Number(action.qty).toLocaleString("ru-RU")} {action.uom}
				</span>
				<span className="text-muted-foreground">{action.targetDate}</span>
			</div>
			{action.estimatedCostCents > 0 && (
				<div className="mt-1">
					<MoneyDisplay
						amount={String(Number(action.estimatedCostCents) / 100)}
						currency={action.currency || "RUB"}
						className="text-[11px]"
					/>
				</div>
			)}
		</div>
	);
}

function PlanningBoardPage() {
	const { t } = useTranslation("manufacturing");
	const navigate = useNavigate();
	const approve = useApprovePlannedAction();

	// Load all actions (not paginated for board — typically < 200 visible)
	const { data } = useListPlannedActions({});
	const actions = (data?.actions ?? []) as (PlannedAction & { id: string })[];

	const handleColumnChange = useCallback(
		(itemId: string, newColumnId: string) => {
			if (newColumnId === "approved") {
				approve.mutate({ id: itemId });
			}
			// Cancel and execute are handled by workflows, not drag
		},
		[approve],
	);

	const handleItemClick = useCallback(
		(id: string) => {
			const action = actions.find((a) => a.id === id);
			if (action?.productId) {
				navigate({
					to: "/manufacturing/products/$id",
					params: { id: action.productId },
				});
			}
		},
		[actions, navigate],
	);

	const [searchText, setSearchText] = useState("");
	const filteredActions = useMemo(
		() =>
			actions.filter((a) =>
				searchText
					? (a.productId ?? "").toLowerCase().includes(searchText.toLowerCase()) ||
						t(`type_${a.actionType}`)
							.toLowerCase()
							.includes(searchText.toLowerCase())
					: true,
			),
		[actions, searchText, t],
	);

	const statusBar = useMemo(() => {
		const counts = COLUMNS.map((col) => {
			const count = actions.filter((a) => a.status === col.id).length;
			return `${col.label}: ${count}`;
		}).join(" • ");
		return [{ id: "counts", content: counts, align: "left" as const }];
	}, [actions]);

	useWorkspacePage({ statusBar });

	return (
		<div className="flex h-full flex-col">
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("search_placeholder", { defaultValue: "Поиск..." })}
						value={searchText}
						onChange={(e) => setSearchText(e.target.value)}
						className="pl-9 h-9"
					/>
				</div>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => navigate({ to: "/manufacturing/actions" })}
				>
					{t("actions")}
				</Button>
			</div>
			<div className="flex-1 p-2 min-h-0">
			<KanbanBoard
				items={filteredActions}
				columns={COLUMNS}
				getColumnId={(item) => item.status}
				renderCard={(item) => <ActionCard action={item} />}
				onColumnChange={handleColumnChange}
				onItemClick={handleItemClick}
				transitions={TRANSITIONS}
				emptyLabel={t("empty_no_actions")}
			/>
			</div>
		</div>
	);
}
